import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Checkbox,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate, Link } from "react-router-dom";
import { RegisterUser } from "../services/user_services.tsx";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// slike
const background = require("../assets/registration/background.png");
const frame = require("../assets/registration/registration_form.png");
const signUpButton = require("../assets/registration/sign_up_button.png");
const coin = require("../assets/registration/coin.png");
const coins = require("../assets/registration/coins.png");
const coinsTop = require("../assets/registration/coins_on_the_top.png");
const king = require("../assets/registration/king.png");

const Registration: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    repeatedPassword: "",
  });

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatedPassword, setShowRepeatedPassword] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 725);

  const [loading, setLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    const images = [background, frame, signUpButton, coin, coins, coinsTop, king];

    let loaded = 0;

    images.forEach((src) => {
      const img = new Image();
      img.src = src;

      const done = () => {
        loaded++;
        if (loaded === images.length) {
          setImagesLoaded(true);
        }
      };

      img.onload = done;
      img.onerror = done;
    });
  }, []);

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 725;
      setIsMobile(mobile);

      if (mobile) {
        setFormData((prev) => ({
          ...prev,
          username: prev.username || "Username",
          email: prev.email || "Email",
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          username: prev.username === "Username" ? "" : prev.username,
          email: prev.email === "Email" ? "" : prev.email,
        }));
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleFocus = (field: string) => {
    if (isMobile) {
      if (field === "username" && formData.username === "Username") {
        setFormData({ ...formData, username: "" });
      }
      if (field === "email" && formData.email === "Email") {
        setFormData({ ...formData, email: "" });
      }
    }
  };

  const handleBlur = (field: string) => {
    if (isMobile) {
      if (field === "username" && !formData.username) {
        setFormData({ ...formData, username: "Username" });
      }
      if (field === "email" && !formData.email) {
        setFormData({ ...formData, email: "Email" });
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    setError(null);

    if (!acceptedTerms) {
      setError("You must accept Terms and Conditions.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email)) {
      setError("Email must be in format example@email.com.");
      return;
    }

    if (formData.password !== formData.repeatedPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      await RegisterUser({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        repeated_password: formData.repeatedPassword,
      });

      navigate("/login");
    } catch (err: any) {
      setError(err.message || "Registration failed.");
    }
  };

  if (loading || !imagesLoaded) {
    return (
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "black",
        }}
      >
        <CircularProgress size={80} />
      </Box>
    );
  }

  return (
    <Box sx={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", overflow: "hidden" }}>
      
      <style>
      {`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          transition: background-color 9999s ease-in-out 0s;
          -webkit-text-fill-color: #ccc !important;
          caret-color: #ccc !important;
        }
      `}
      </style>

      <img src={background} style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", zIndex: 0 }} />

      <Box sx={{ position: "absolute", left: 0, bottom: 0, width: "40%", zIndex: 1, display: { xs: "none", md: "block" } }}>
        <img src={coins} style={{ width: "100%", opacity: 0.5 }} />
      </Box>

      <Box sx={{ position: "absolute", top: 0, left: 0, width: "18%", zIndex: 1, display: { xs: "none", md: "block" } }}>
        <img src={coinsTop} style={{ width: "100%", opacity: 0.5 }} />
      </Box>

      <Box sx={{ position: "absolute", right: 0, bottom: 0, height: "80%", zIndex: 1, display: { xs: "none", md: "block" } }}>
        <img src={king} style={{ height: "100%", opacity: 0.5 }} />
      </Box>

      <Box sx={{ position: "absolute", top: "52%", left: "50%", transform: "translate(-50%, -50%)", width: "520px", maxWidth: "90vw", zIndex: 2 }}>
        
        <Box sx={{ position: "absolute", top: "-23%", left: "50%", transform: "translateX(-50%)", width: "90%", zIndex: 1, pointerEvents: "none", display: { xs: "none", md: "block" } }}>
          <img src={coin} style={{ width: "100%", opacity: 0.2 }} />
        </Box>

        <Box sx={{ position: "relative", zIndex: 2, display: "block", "@media (max-width:725px)": { display: "none" } }}>
          <img src={frame} style={{ width: "100%" }} />
        </Box>

        <Box sx={{ position: "absolute", top: "56.5%", left: "50%", transform: "translate(-50%, -50%)", width: "75%", display: "flex", flexDirection: "column", alignItems: "center", zIndex: 3 }}>
          
          {["username", "email", "password", "repeatedPassword"].map((field) => (
            <TextField
              key={field}
              fullWidth
              name={field}
              value={(formData as any)[field]}
              onChange={handleChange}
              onFocus={() => handleFocus(field)}
              onBlur={() => handleBlur(field)}
              autoComplete="new-password"
              type={
                field === "password"
                  ? showPassword ? "text" : "password"
                  : field === "repeatedPassword"
                  ? showRepeatedPassword ? "text" : "password"
                  : "text"
              }
              InputProps={
                field === "password" || field === "repeatedPassword"
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              field === "password"
                                ? setShowPassword(!showPassword)
                                : setShowRepeatedPassword(!showRepeatedPassword)
                            }
                          >
                            {(field === "password" ? showPassword : showRepeatedPassword)
                              ? <VisibilityOff />
                              : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
              sx={{
                mb: field === "repeatedPassword" ? 0.5 : 2.5,
                input: {
                  color: "#ccc",
                  fontFamily: "'Cinzel', serif",
                  fontSize: "16px",
                  paddingLeft: "12px",
                },
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  borderRadius: "8px",
                  background: "#1a0026",
                  "& fieldset": { border: "2px solid #555" },
                  "&:hover fieldset": { borderColor: "#ff00cc" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#ff00cc",
                    boxShadow: "0 0 10px #ff00cc",
                  },
                },
              }}
            />
          ))}

          <Box sx={{ display: "flex", alignItems: "center", alignSelf: "flex-start", backgroundColor: "#1a0026", px: 0.2, py: 0.1 }}>
            <Checkbox
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              sx={{ transform: "scale(1.1)", color: "#ff00cc", padding: "4px" }}
            />

            {isMobile && (
              <Typography sx={{ color: "#ccc", fontSize: "12px" }}>
                I agree to Terms and Conditions
              </Typography>
            )}
          </Box>

          <Box onClick={handleSubmit} sx={{ width: "70%", cursor: "pointer", mt: 1, "&:hover": { transform: "scale(1.05)", filter: "drop-shadow(0 0 15px #ff00cc)" } }}>
            <img src={signUpButton} style={{ width: "100%" }} />
          </Box>

          <Box sx={{ mt: 1, background: "linear-gradient(180deg, #5a0f7a 0%, #3a0050 100%)", padding: "6px 12px", borderRadius: "6px" }}>
            <Typography sx={{ color: "#fff" }}>
              Already have an account?{" "}
              <Link to="/login" style={{ color: "#ff66ff", fontWeight: "bold", textDecoration: "underline" }}>
                Log in
              </Link>
            </Typography>
          </Box>

          {isMobile && error && (
            <Box sx={{ mt: 1, background: "#1a0026", padding: "6px 12px", borderRadius: "6px" }}>
              <Typography sx={{ color: "#ff4d6d" }}>{error}</Typography>
            </Box>
          )}
        </Box>

        {!isMobile && error && (
          <Box sx={{ position: "absolute", bottom: "-50px", left: "50%", transform: "translateX(-50%)", background: "#1a0026", padding: "6px 12px", borderRadius: "6px" }}>
            <Typography sx={{ color: "#ff4d6d" }}>{error}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Registration;